<template>


  <SkBasicRows
    v-if="isLoading"
  />

  <div v-else>
    
    <div class="modal_user_content">

      <div class="image content content_profile">
 

        <UserAvatarSelector
          v-if="userCommonData?.id"
          :initial-avatar="userCommonData.avatar_small"
          @update:file="handleAvatarFileUpdate"
          @update:num="handleAvatarNumUpdate"
        />
          

        <div class="settings_description">

          <div class="row">
            <div class="field_input">
              <UiInput
                :label="$t('profile_menu_edit_profile_username')"
                :text="form.name.value"
                :error="nameError"
                :locator="'signup_view_username_locator'"
                @change:text="form.name.value = $event"
                @input="markInteracted('name')"
                @blur="markDirty('name')"
              />
            </div>
          </div>


          <div class="row">
            <div class="field_input">
              <UiInput
                :label="$t('profile_menu_edit_profile_email')"
                :text="form.email.value"
                :error="emailError"
                :locator="'signup_view_email_locator'"
                @change:text="form.email.value = $event"
                @input="markInteracted('email')"
                @blur="markDirty('email')"
              />
              <i class="icon at"></i>
            </div>
          </div>

          <div class="row">
            <div class="field_input">
              <UiInput
                :label="$t('profile_menu_edit_profile_note')"
                :text="form.slogan.value"
                @change:text="form.slogan.value = $event"
                placeholder="Note..."
              />
            </div>
          </div>
 
 
          <div class="row">
            <div class="label">
              <UiToggleV2
                v-model="notifications"
                @update:model-value="updateNotifications"
                :label="$t('profile_menu_edit_profile_checkbox_send_me')"
              />
            </div>
          </div>

          <br />

          <div v-if="generalError" class="general-error-block">
            {{ generalError }}
          </div>

          <UiButton
            :name="$t('universal_button_save')"
            :type="'primary'"
            :locator="'universal_button_save_locator'"
            @click="saveProfile"
            :is-loading="isSaving"
            />        

 

        </div>

      </div>

    </div>
 
  </div>

    

</template>

<script setup lang="ts">

import type { UserSettings, User } from '@/types/user'
import { useFormValidation } from '~/composables/useFormValidation'
import { profileFormConfig } from '~/types/validation/formsConfig'

const { t } = useI18n();
const route = useRoute();

const router = useRouter();
const {$api} = useNuxtApp()

interface Props {
  user: User,
  userId: number,
}

const props = defineProps<Props>();


const isSaving = ref<boolean>(false);
const oldNickName = ref<string>('');
const generalError = ref('')

const form = reactive({
  name: {
    value: '',
    isInteracted: false,
    isDirty: false
  },
  email: {
    value: '',
    isInteracted: false,
    isDirty: false
  },
  slogan: {
    value: '',
    isInteracted: false,
    isDirty: false
  }
})

const notifications = ref(false)

const { validateField, markInteracted, markDirty, validateForm, validateOptionalForm } = useFormValidation(form, profileFormConfig)

const validation = {
  name: computed(() => validateField('name')),
  email: computed(() => validateField('email'))
}

const backendErrors = reactive({
  name: '',
  email: '',
  slogan: ''
})

const nameError = computed(() => backendErrors.name || validation.name.value.errorMessage)
const emailError = computed(() => backendErrors.email || validation.email.value.errorMessage)

const validate = () => validateForm()

const redirectIfNewName = function(){
  if (!userCommonData.value) return;
  
  if(oldNickName.value != userCommonData.value.name){
    router.push('/grower/' + userCommonData.value.name+'/settings');
  }
}

interface AvatarFile {
  file: File;
  name?: string;
}

const newAvatarFile = ref<AvatarFile | null>(null);
const newAvatarNum = ref<number | null>(null);

const handleAvatarFileUpdate = async (file: AvatarFile) => {
  newAvatarFile.value = file;
  newAvatarNum.value = null;
};

const handleAvatarNumUpdate = async (num: number) => {
  newAvatarNum.value = num;
  newAvatarFile.value = null;
};

interface AvatarResponse {
  data: string;
}

const saveProfile = async function() {
  if (!userCommonData.value) return;
  
  backendErrors.name = ''
  backendErrors.email = ''

  const hasFormChanges =
    form.name.value !== userCommonData.value.name ||
    form.email.value !== userCommonData.value.email ||
    form.slogan.value !== (userCommonData.value.slogan ?? '')

  const hasNotificationsChanged =
    Boolean(userCommonData.value.enable_email_notifications) !== notifications.value

  const hasAvatarChanged = newAvatarFile.value !== null || newAvatarNum.value !== null
  const changedFields = []
  if (form.name.value !== userCommonData.value.name) changedFields.push('name')
  if (form.email.value !== userCommonData.value.email) changedFields.push('email')

  if (hasFormChanges && !validateOptionalForm(changedFields)) {
    return;
  }

  if (!hasFormChanges && !hasNotificationsChanged && !hasAvatarChanged) {
    return;
  }

  isSaving.value = true;
  try {
    const updatedData = {
      ...userCommonData.value,
      name: form.name.value,
      email: form.email.value,
      slogan: form.slogan.value,
      enable_email_notifications: notifications.value ? 1 : 0,
      avatar_num: newAvatarFile.value ? null : (newAvatarNum.value !== null ? newAvatarNum.value : userCommonData.value.avatar_num)
    }

    await $api.putGrowerProfile(userCommonData.value.id, updatedData);
    
    userCommonData.value = {
      ...userCommonData.value,
      name: form.name.value,
      email: form.email.value,
      slogan: form.slogan.value,
      enable_email_notifications: notifications.value ? 1 : 0
    };

    if (newAvatarFile.value) {
      const formData = new FormData();
      formData.append('file', newAvatarFile.value.file, newAvatarFile.value.file.name);
      const response = await $api.postGrowersAvatars(userCommonData.value.id, formData) as AvatarResponse;
      if (response?.data) {
        userCommonData.value.avatar_small = response.data;
      }
    } else if (newAvatarNum.value !== null) {
      const response = await fetch('/avatars/' + newAvatarNum.value + '.jpg');
      const blob = await response.blob();
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
      const formData = new FormData();
      formData.append('file', file, file.name);
      const uploadResponse = await $api.postGrowersAvatars(userCommonData.value.id, formData) as AvatarResponse;
      if (uploadResponse?.data) {
        userCommonData.value.avatar_small = uploadResponse.data;
      }
    }

    useToast().success(t('profile_menu_edit_profile_message_save_profile'));
    redirectIfNewName();
    useAuth().refresh();
  } catch (error: any) {
    switch (error.message) {
      case 'Update user fail. Username is already in use':
        form.name.isDirty = true
        form.name.isInteracted = true
        backendErrors.name = t('signup_view_error_username_in_use')
        break
      case 'Email is already in use':
        form.email.isDirty = true
        form.email.isInteracted = true
        backendErrors.email = t('signup_view_error_email_in_use')
        break
      default:
        generalError.value = t('universal_generic_error')
    }
  } finally {
    isSaving.value = false;
  }
}



const updateNotifications = (value: boolean) => {
  if (userCommonData.value) {
    notifications.value = value
    userCommonData.value.enable_email_notifications = value ? 1 : 0
  }
}


const userCommonData = ref<UserSettings | null>(null)
const isLoading = ref(true)

const fetchUserData = async () => {
  try {
    const response = await $api.getGrowerSettings(props.userId) as UserSettings
    userCommonData.value = response
    oldNickName.value = response.name

    form.name.value = response.name
    form.email.value = response.email
    form.slogan.value = response.slogan || ''
    notifications.value = response.enable_email_notifications
  } catch (error) {
    generalError.value = t('universal_generic_error')
  } finally {
    isLoading.value = false
  }
}

onMounted(fetchUserData)
</script>



<style scoped>

.content_profile {
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
}

.modal_user_content .settings_description {
    display: inline-block;
    width: calc(100% - 230px);
    vertical-align: middle;
    margin-right: auto;
}

.modal_user_content .settings_avatar {
    display: inline-block;
    vertical-align: top;
    width: 180px;
    margin-left: 30px;
    position: relative;
}
 
.modal_user_content .ava{
  width: 180px;
  flex-direction: column;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.modal_user_content .ava .img{
  aspect-ratio: 1/1;
  width: 100%;
  border-radius: 10px;
    border-radius: 100%;
}


.modal_user_content .btn_black {
    background-color: #1B1C1D;
    color: #FFFFFF;
    text-shadow: none;
}

.modal_user_content .save_profile_btn{
    background-color: #21BA45;
    color: #FFFFFF;
    text-shadow: none;
}

.modal_user_content .settings_description .row{
    display: flex;
    width: 100%;
    margin-top: 15px;
}

.modal_user_content .settings_description .label{
    display: inline-block;
    min-width: 280px;
}

.modal_user_content .settings_description .field_input{
    display: inline-block;
    width: 100%;
}

.modal_user_content .settings_description .field_input input{
    width: 100%;
}



@container pb (max-width: 768px) {

  .modal_user_content .ava{
    justify-content: center;
    margin: 0 auto;
  }
  .content_profile{
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  .modal_user_content .settings_avatar{

      margin-left: auto;
      margin-right: auto;
  }

  .modal_user_content .settings_description{
    display: block;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    margin-top: 20px;
  }

  .modal_user_content .settings_description .row{
    display: flex;
    flex-direction: column;
  }

}


</style>
